// Thin fetch wrapper: same-origin cookies, JSON in/out, CSRF header on mutations.
// The CSRF token lives only in memory; it is refreshed whenever a SessionInfo arrives.

export class ApiError extends Error {
  constructor(
    public status: number,
    /** Machine-readable error code from `{ error }`, or "http_<status>". */
    public code: string,
    public body: unknown,
  ) {
    super(`${status} ${code}`);
    this.name = "ApiError";
  }

  /** Human-readable `message` from the error body, if the server sent one. */
  get serverMessage(): string | null {
    if (this.body && typeof this.body === "object" && "message" in this.body) {
      const value = (this.body as Record<string, unknown>).message;
      return typeof value === "string" && value.trim() ? value.trim() : null;
    }
    return null;
  }

  /** Text field from the error body (e.g. `reason`), if present. */
  stringField(name: string): string | null {
    if (this.body && typeof this.body === "object" && name in this.body) {
      const value = (this.body as Record<string, unknown>)[name];
      return typeof value === "string" ? value : null;
    }
    return null;
  }

  /** Numeric field from the error body (e.g. `retryAfterSeconds`), if present. */
  numberField(name: string): number | null {
    if (this.body && typeof this.body === "object" && name in this.body) {
      const value = (this.body as Record<string, unknown>)[name];
      return typeof value === "number" && Number.isFinite(value) ? value : null;
    }
    return null;
  }
}

let csrfToken: string | null = null;
let onUnauthorized: (() => void) | null = null;

export function setCsrfToken(token: string | null | undefined): void {
  csrfToken = token ?? null;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** Called when a non-auth request returns 401 (session expired or revoked). */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** The reader's IANA time zone; the server parses "tomorrow 9am" in it. */
export function userTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}

const timeZone = userTimeZone();

export interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** A file sent as the raw request body (its own content type and headers), not JSON. */
  upload?: { file: Blob; headers: Record<string, string> };
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };
  if (options.body !== undefined) headers["content-type"] = "application/json";
  if (options.upload) {
    headers["content-type"] = options.upload.file.type || "application/octet-stream";
    Object.assign(headers, options.upload.headers);
  }
  if (method !== "GET" && csrfToken) headers["x-csrf-token"] = csrfToken;
  if (timeZone) headers["x-user-timezone"] = timeZone;

  const res = await fetch(path, {
    method,
    headers,
    credentials: "same-origin",
    body: options.upload
      ? options.upload.file
      : options.body === undefined
        ? undefined
        : JSON.stringify(options.body),
    signal: options.signal,
  });

  const body = await readBody(res);
  if (!res.ok) {
    const code = errorCode(body) ?? `http_${res.status}`;
    if (res.status === 401 && !path.startsWith("/api/auth/")) onUnauthorized?.();
    throw new ApiError(res.status, code, body);
  }
  return body as T;
}

async function readBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function errorCode(body: unknown): string | null {
  if (body && typeof body === "object" && "error" in body) {
    const value = (body as { error: unknown }).error;
    if (typeof value === "string" && value) return value;
  }
  return null;
}

export function isApiError(error: unknown, code?: string): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}
